import { forwardRef } from 'react';
import type { InputHTMLAttributes } from 'react';
import { cx } from '../lib/cx';
import { Icon } from '../icons/Icon';
import { useControllableState } from '../lib/hooks';

export interface SearchInputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'defaultValue' | 'onChange' | 'type'> {
  value?: string;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
  /** Show the clear (✕) button when there's text. Default true. */
  clearable?: boolean;
  /** Accessible name for the clear ✕. English default. */
  clearLabel?: string;
}

/** Search field with a leading magnifier and a clear affordance (§7.7 clients). */
export const SearchInput = forwardRef<HTMLInputElement, SearchInputProps>(function SearchInput(
  {
    value,
    defaultValue = '',
    onValueChange,
    clearable = true,
    className,
    placeholder = 'Search…',
    clearLabel = 'Clear search',
    'aria-label': ariaLabel,
    ...rest
  },
  ref,
) {
  const [val, setVal] = useControllableState<string>(value, defaultValue, onValueChange);
  const showClear = clearable && val.length > 0;
  return (
    <div className={cx('tc-input-group', 'tc-input-group--has-start', showClear && 'tc-input-group--has-end', className)}>
      <span className="tc-input-group__icon tc-input-group__icon--start" aria-hidden>
        <Icon name="search" size={18} />
      </span>
      <input
        ref={ref}
        type="search"
        role="searchbox"
        className="tc-input"
        aria-label={ariaLabel ?? (typeof placeholder === 'string' ? placeholder : 'Search')}
        placeholder={placeholder}
        value={val}
        onChange={(e) => setVal(e.target.value)}
        {...rest}
      />
      {showClear && (
        <button type="button" className="tc-input-group__clear" aria-label={clearLabel} onClick={() => setVal('')}>
          <Icon name="x" size={16} />
        </button>
      )}
    </div>
  );
});
